import {WALLET, PERKS, FLEXIREWARDS, RECOGNITION} from "@irati-web/app/src/routeConfig"
import ManagerTriggerModal from "@irati-web/wallet/src/components/ManagerRewardsModal"
import RewardsTriggerModal from "@irati-web/wallet/src/components/RewardsModal/RewardsTriggerModal"
import TreatsTriggerModal from "@irati-web/wallet/src/components/TreatsModal/TreatsTriggerModal"
import authentication from "@irati/auth"
import {PaymentCurrencyType, Wallet, WalletEntityType} from "@irati/types"
import FlexiPointsIcon from "@irati/ui/images/flexi_point.svg"
import RewardPointsIcon from "@irati/ui/images/reward_point.svg"
import {IconButton} from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import {Card, CardProps} from "@irati/ui/src/Layout"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import Loading from "@irati/ui/src/Loading"
import {Body, H3, H6} from "@irati/ui/src/Typography"
import FormattedMessage, {FormattedNumber} from "@irati/utils/lang"
import {hangPromise} from "@irati/utils/promise"
import walletService from "@irati/wallet/graph/wallet"
import React, {useEffect, useState} from "react"
import styled, {css} from "styled-components"
import perksTracking from "@irati-tracking/perks/perks"
import {walletTracking, rewardPointsWalletTracking} from "@irati-tracking/wallet"
import {WalletNames, Context} from "./shared"

interface Tile {
  primary?: boolean
  Title: React.FC
  SubTitle: React.FC
  currency: PaymentCurrencyType
  icon: string
  LearnMore: React.FC
  request: (uuid: string) => Promise<Wallet>
}

interface CTA {
  Text: React.FC
  app: string
  target: string
  onClick?: () => void
}

enum WalletState {
  FLEXI,
  USER_REWARD,
  EMPTY_GIVE,
  MANAGER_GIVE,
  ADMIN_GIVE
}

const ctaForState: Record<WalletState, CTA> = {
  [WalletState.FLEXI]: {
    Text: () => <FormattedMessage id="platform.homepage.lanes.wallet.flexi.cta" />,
    target: "/categories/flexi-perks",
    app: PERKS,
    onClick: () => perksTracking.viewCategory("Flexi Perks", false, "platform_homepage")
  },
  [WalletState.USER_REWARD]: {
    Text: () => <FormattedMessage id="platform.homepage.lanes.wallet.rewards.cta" />,
    app: FLEXIREWARDS,
    target: "/categories",
    onClick: () => walletTracking.clickBrowseRewards("platform_homepage")
  },
  [WalletState.EMPTY_GIVE]: {
    Text: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.zeroPoints" />,
    target: "/flexi-checkout/top-up",
    app: WALLET,
    onClick: () => rewardPointsWalletTracking.buyPoints({source: "platform_homepage"})
  },
  [WalletState.MANAGER_GIVE]: {
    Text: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.cta" />,
    target: "/polls/create",
    app: RECOGNITION
  },
  [WalletState.ADMIN_GIVE]: {
    Text: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.cta" />,
    target: "/flexi-assign/recipients",
    app: WALLET,
    onClick: () => rewardPointsWalletTracking.giveRewardPoints("platform_homepage")
  }
}

const cardContent: Record<WalletNames, Tile> = {
  [WalletNames.GIVE]: {
    primary: true,
    Title: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.title" />,
    SubTitle: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.subTitle" />,
    currency: PaymentCurrencyType.Rp,
    icon: RewardPointsIcon,
    LearnMore: ManagerTriggerModal,
    request: (uuid: string) => walletService.get(uuid, PaymentCurrencyType.Rp, WalletEntityType.Reward, {})
  },
  [WalletNames.REWARDS]: {
    Title: () => <FormattedMessage id="platform.homepage.lanes.wallet.rewards.title" />,
    SubTitle: () => <FormattedMessage id="platform.homepage.lanes.wallet.give.subTitle" />,
    currency: PaymentCurrencyType.Rp,
    icon: RewardPointsIcon,
    LearnMore: RewardsTriggerModal,
    request: (uuid: string) => walletService.get(uuid, PaymentCurrencyType.Rp, WalletEntityType.User, {})
  },
  [WalletNames.FLEXI]: {
    Title: () => <FormattedMessage id="platform.homepage.lanes.wallet.flexi.title" />,
    SubTitle: () => <FormattedMessage id="platform.homepage.lanes.wallet.flexi.subTitle" />,
    currency: PaymentCurrencyType.Tt,
    icon: FlexiPointsIcon,
    LearnMore: TreatsTriggerModal,
    request: (uuid: string) => walletService.get(uuid, PaymentCurrencyType.Tt, WalletEntityType.User, {})
  }
}

const CardLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  align-items: stretch;
  gap: ${({theme}) => theme.spacing.two};
`

const StyledCard = styled(Card)<CardProps & {primary?: boolean}>`
  & ${IconButton} {
    fill: ${({theme, primary}) => (primary ? theme.colors.greys.white : theme.colors.primary1)};
  }

  ${({theme, primary}) =>
    primary &&
    css`
      background-color: ${theme.colors.primary2};

      & p {
        color: ${theme.colors.greys.white} !important;
      }
    `}
`

const ButtonPadding = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
`

export default function WalletCard({name, apps}: {name: WalletNames} & Context) {
  const [wallet, setWallet] = useState<Wallet>({})
  const [load, setLoad] = useState(hangPromise())

  const cta = ctaForState[getWalletState(name, wallet)]
  const content = cardContent[name]

  useEffect(() => {
    const uuid = authentication.user().uuid!
    setLoad(content.request(uuid).then(setWallet))
  }, [name, content])

  return (
    <StyledCard primary={content.primary} padding={1.5} marginBottom>
      <Flex direction="column" gap={0.5}>
        <Flex justifyContent="space-between">
          <H6 as="p" noWrap>
            <content.Title />
          </H6>

          <content.LearnMore />
        </Flex>

        <CardLayout>
          <Flex direction="column" gap={2} flex="1" justifyContent="space-around">
            {/* height is set to minimise layout shift for the majority of customers */}
            <Flex gap={2} alignItems="center" height="50px">
              <img alt="" src={content.icon} width="32px" />

              <Loading load={load}>
                <Flex wrap="wrap" alignItems="baseline" flex="1">
                  <H3 as="p" marginRight={1}>
                    <FormattedNumber value={wallet.availableBalance ?? 0} />
                  </H3>

                  <Body small noWrap>
                    <content.SubTitle />
                  </Body>
                </Flex>
              </Loading>
            </Flex>
          </Flex>

          <ButtonPadding>
            <span />

            <ButtonRouterLink app={apps[cta.app]} full to={cta.target} primary={content.primary} marginBottom={0.5} onClick={cta.onClick}>
              <cta.Text />
            </ButtonRouterLink>
          </ButtonPadding>
        </CardLayout>
      </Flex>
    </StyledCard>
  )
}

function getWalletState(name: WalletNames, wallet?: Wallet): WalletState {
  if (name === WalletNames.REWARDS) return WalletState.USER_REWARD

  if (name === WalletNames.FLEXI) return WalletState.FLEXI

  // if things haven't, or remain unloaded, we want to push managers/admins to give points
  if (!wallet) return WalletState.ADMIN_GIVE

  if (wallet.availableBalance === 0) return WalletState.EMPTY_GIVE

  const isConsideredAdmin = authentication.apps("wallet.flexiRewards.canTopUpRewardPoints")

  return isConsideredAdmin ? WalletState.ADMIN_GIVE : WalletState.MANAGER_GIVE
}
